import React, { useState, useEffect, useRef } from 'react'
import { 
  Download,
  Archive,
  CheckCircle,
  XCircle,
  AlertTriangle,
  DollarSign
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import archivedContractService from '../../services/archivedContractService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const ArchivedContract = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalArchived: 0,
    completedArchived: 0,
    expiredArchived: 0,
    terminatedArchived: 0,
    cancelledArchived: 0,
    supersededArchived: 0,
    totalArchivedValue: 0,
    canBeDeleted: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [contractsResponse, statsResponse] = await Promise.all([
        archivedContractService.getAllArchivedContracts(),
        archivedContractService.getArchivedContractStats()
      ])
      
      if (contractsResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (contractsResponse.data || []).map(contract => ({
          ...contract,
          id: contract._id
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (contractsResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No archived contract records found.')
        }
      } else {
        toast.error(contractsResponse.message || 'Failed to load archived contracts')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading archived contract records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view archived contracts')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to load archived contract records')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const getArchiveReasonBadgeClass = (reason) => {
    const reasonMap = {
      'Completed': 'active',
      'Expired': 'inactive',
      'Terminated': 'danger',
      'Cancelled': 'inactive',
      'Superseded': 'warning',
      'Other': 'inactive'
    }
    return reasonMap[reason] || 'inactive'
  }

  const columns = [
    {
      accessorKey: 'contractNumber',
      header: 'Contract Number',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'contractTitle',
      header: 'Contract Title',
      cell: ({ getValue }) => (
        <div className="service-description-cell" style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
          <span className="description-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'partyName',
      header: 'Party',
      cell: ({ getValue, row }) => (
        <div className="service-description-cell" style={{ textAlign: 'left' }}>
          <span className="description-text">{getValue() || '-'}</span>
          {row.original.partyCode && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{row.original.partyCode}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'archiveDate',
      header: 'Archived Date',
      cell: ({ getValue }) => {
        const date = getValue()
        return (
          <div style={{ textAlign: 'center' }}>
            {date ? new Date(date).toLocaleDateString() : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'archiveReason',
      header: 'Archive Reason',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getArchiveReasonBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'contractValue',
      header: 'Contract Value',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {formatCurrency(getValue(), row.original.currency || 'INR')}
        </div>
      ),
    },
    {
      accessorKey: 'canBeDeleted',
      header: 'Can Delete',
      cell: ({ getValue, row }) => {
        const canDelete = getValue()
        const retentionEndDate = row.original.retentionEndDate
        return (
          <div style={{ textAlign: 'center' }}>
            {canDelete ? (
              <span className="status-badge danger">Yes</span>
            ) : (
              <span className="status-badge inactive">
                {retentionEndDate ? `Until ${new Date(retentionEndDate).toLocaleDateString()}` : 'No'}
              </span>
            )}
          </div>
        )
      },
    },
  ]

  const handleView = (contractData) => {
    navigate('/contracts/archived-contract/view', { state: { contractData } })
  }

  const handleDelete = (contractData) => {
    if (!contractData.canBeDeleted) {
      toast.warning('This contract cannot be deleted. Retention period has not expired yet.')
      return
    }
    setDeleteItem(contractData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting archived contract...')
      const response = await archivedContractService.deleteArchivedContract(deleteItem._id)
      if (response.success) {
        toast.success('Archived contract deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete archived contract')
      }
    } catch (error) {
      console.error('Error deleting archived contract:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Contract cannot be deleted. Retention period has not expired.')
      } else if (error.response?.status === 404) {
        toast.error('Archived contract not found')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this archived contract')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to delete archived contract. Please try again.')
        }
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Contracts', path: '/contracts' },
          { label: 'Archived Contracts', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>Archived Contracts</h1>
          <p>Completed and archived contracts</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <Archive size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalArchived || 0}</div>
          <div className="metric-title">Total Archived</div>
          <div className="metric-subtitle">All archived contracts</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Done</span>
            </div>
          </div>
          <div className="metric-value">{stats.completedArchived || 0}</div>
          <div className="metric-title">Completed</div>
          <div className="metric-subtitle">Successfully completed</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <XCircle size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Expired</span>
            </div>
          </div>
          <div className="metric-value">{stats.expiredArchived || 0}</div>
          <div className="metric-title">Expired</div>
          <div className="metric-subtitle">Past end date</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <XCircle size={16} />
            </div>
            <div className="metric-trend negative">
              <span>Ended</span>
            </div>
          </div>
          <div className="metric-value">{stats.terminatedArchived || 0}</div>
          <div className="metric-title">Terminated</div>
          <div className="metric-subtitle">Early termination</div>
        </div>

        <div className="metric-card conversion">
          <div className="metric-header">
            <div className="metric-icon">
              <AlertTriangle size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Eligible</span>
            </div>
          </div>
          <div className="metric-value">{stats.canBeDeleted || 0}</div>
          <div className="metric-title">Can Be Deleted</div>
          <div className="metric-subtitle">Eligible for removal</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Value</span>
            </div>
          </div>
          <div className="metric-value">{formatCurrency(stats.totalArchivedValue)}</div>
          <div className="metric-title">Total Value</div>
          <div className="metric-subtitle">Archived contract value</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onView={handleView}
          onDelete={handleDelete}
          enableSelection={false}
          hideEdit={true}
          noCard={true}
          pageSize={10}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Archived Contract"
        message={`Are you sure you want to permanently delete archived contract "${deleteItem?.contractNumber}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default ArchivedContract

